import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

/**
 * Before/After Resume Comparison View
 * Shows the transformation from generic resume to tailored resume
 * This is the "Holy Shit" visual that makes users want to share
 */
const BeforeAfterView = ({
  originalResume,
  tailoredResume,
  keywordAnalysis,
  atsScoreOriginal = null,
  atsScoreTailored
}) => {
  const [viewMode, setViewMode] = useState('split'); // 'split', 'before', 'after', 'slider'
  const [sliderPosition, setSliderPosition] = useState(50);

  // Extract relevant sections for comparison
  const beforeSummary = originalResume?.professionalSummary || originalResume?.summary || '';
  const afterSummary = tailoredResume?.tailoredContent?.summary || '';

  const beforeSkills = originalResume?.skills || [];
  const afterSkills = tailoredResume?.tailoredContent?.skills || [];

  const matchedKeywords = keywordAnalysis?.matched || [];
  const missingKeywords = keywordAnalysis?.missing || [];
  const addedKeywords = keywordAnalysis?.added || [];

  // Highlight keywords in text
  const highlightKeywords = (text, keywords, className) => {
    if (!text || !keywords.length) return text;

    let result = text;
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b(${keyword})\\b`, 'gi');
      result = result.replace(regex, `<mark class="${className}">$1</mark>`);
    });
    return result;
  };

  const ViewToggle = () => (
    <div className="flex items-center justify-center gap-2 mb-4">
      <button
        onClick={() => setViewMode('split')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          viewMode === 'split'
            ? 'bg-primary-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Side by Side
      </button>
      <button
        onClick={() => setViewMode('slider')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          viewMode === 'slider'
            ? 'bg-primary-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        <ArrowsRightLeftIcon className="h-4 w-4 inline mr-1" />
        Slider
      </button>
    </div>
  );

  const KeywordBadge = ({ keyword, type }) => {
    const styles = {
      matched: 'bg-green-100 text-green-800 border-green-200',
      missing: 'bg-red-100 text-red-800 border-red-200',
      added: 'bg-blue-100 text-blue-800 border-blue-200'
    };

    const icons = {
      matched: <CheckCircleIcon className="h-3 w-3" />,
      missing: <XCircleIcon className="h-3 w-3" />,
      added: <SparklesIcon className="h-3 w-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${styles[type]}`}>
        {icons[type]}
        {keyword}
      </span>
    );
  };

  const ScoreComparison = () => (
    <div className="flex items-center justify-center gap-8 py-4 bg-gradient-to-r from-gray-50 via-white to-green-50 rounded-lg mb-6">
      {atsScoreOriginal !== null && (
        <>
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Before</p>
            <p className={`text-3xl font-bold ${
              atsScoreOriginal >= 80 ? 'text-green-600' :
              atsScoreOriginal >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {atsScoreOriginal}%
            </p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-0.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-500 rounded" />
            <svg className="w-6 h-6 text-green-500 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </>
      )}
      <div className="text-center">
        <p className="text-sm text-gray-500 mb-1">After</p>
        <p className={`text-3xl font-bold ${
          atsScoreTailored >= 80 ? 'text-green-600' :
          atsScoreTailored >= 60 ? 'text-yellow-600' : 'text-red-600'
        }`}>
          {atsScoreTailored}%
        </p>
      </div>
      {atsScoreOriginal !== null && (
        <div className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-semibold">
          +{atsScoreTailored - atsScoreOriginal}% improvement
        </div>
      )}
    </div>
  );

  const SummaryComparison = () => (
    <div className="grid md:grid-cols-2 gap-4 mb-6">
      {/* Before */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-400" />
            Original Summary
          </h4>
        </div>
        <div className="p-4 bg-gray-50">
          <p className="text-sm text-gray-600 leading-relaxed">
            {beforeSummary || 'No summary in original resume'}
          </p>
        </div>
      </div>

      {/* After */}
      <div className="border border-green-200 rounded-lg overflow-hidden">
        <div className="bg-green-50 px-4 py-2 border-b border-green-200">
          <h4 className="font-medium text-green-700 flex items-center gap-2">
            <SparklesIcon className="h-4 w-4" />
            Tailored Summary
          </h4>
        </div>
        <div className="p-4 bg-green-50/50">
          <p
            className="text-sm text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: highlightKeywords(
                afterSummary,
                matchedKeywords,
                'bg-green-200 text-green-900 px-0.5 rounded'
              )
            }}
          />
        </div>
      </div>
    </div>
  );

  const SkillsComparison = () => (
    <div className="grid md:grid-cols-2 gap-4 mb-6">
      {/* Before */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
          <h4 className="font-medium text-gray-700">Original Skills Order</h4>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {beforeSkills.slice(0, 10).map((skill, i) => (
            <span
              key={i}
              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
            >
              {skill}
            </span>
          ))}
          {beforeSkills.length > 10 && (
            <span className="px-2 py-1 text-gray-500 text-sm">
              +{beforeSkills.length - 10} more
            </span>
          )}
        </div>
      </div>

      {/* After */}
      <div className="border border-green-200 rounded-lg overflow-hidden">
        <div className="bg-green-50 px-4 py-2 border-b border-green-200">
          <h4 className="font-medium text-green-700 flex items-center gap-2">
            <SparklesIcon className="h-4 w-4" />
            Optimized Skills (Reordered for Job)
          </h4>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {afterSkills.slice(0, 10).map((skill, i) => {
            const isMatched = matchedKeywords.some(
              k => skill.toLowerCase().includes(k.toLowerCase())
            );
            return (
              <span
                key={i}
                className={`px-2 py-1 rounded text-sm ${
                  isMatched
                    ? 'bg-green-100 text-green-800 font-medium'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {i < 3 && <span className="text-green-600 mr-1">★</span>}
                {skill}
              </span>
            );
          })}
        </div>
        <div className="px-4 pb-4 text-xs text-gray-500">
          ★ Top skills prioritized for this job
        </div>
      </div>
    </div>
  );

  const KeywordSummary = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="font-medium text-gray-900 mb-3">Keyword Analysis</h4>

      <div className="space-y-3">
        {/* Matched */}
        <div>
          <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            Matched Keywords ({matchedKeywords.length})
          </p>
          <div className="flex flex-wrap gap-1">
            {matchedKeywords.map((kw, i) => (
              <KeywordBadge key={i} keyword={kw} type="matched" />
            ))}
          </div>
        </div>

        {/* Added */}
        {addedKeywords.length > 0 && (
          <div>
            <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
              <SparklesIcon className="h-4 w-4 text-blue-500" />
              Keywords Added ({addedKeywords.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {addedKeywords.map((kw, i) => (
                <KeywordBadge key={i} keyword={kw} type="added" />
              ))}
            </div>
          </div>
        )}

        {/* Missing */}
        {missingKeywords.length > 0 && (
          <div>
            <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
              <XCircleIcon className="h-4 w-4 text-red-500" />
              Still Missing ({missingKeywords.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {missingKeywords.map((kw, i) => (
                <KeywordBadge key={i} keyword={kw} type="missing" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* View toggle */}
      <ViewToggle />

      {/* Score comparison banner */}
      <ScoreComparison />

      {/* Summary comparison */}
      {(beforeSummary || afterSummary) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional Summary</h3>
          <SummaryComparison />
        </div>
      )}

      {/* Skills comparison */}
      {(beforeSkills.length > 0 || afterSkills.length > 0) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Skills</h3>
          <SkillsComparison />
        </div>
      )}

      {/* Keyword summary */}
      {(matchedKeywords.length > 0 || missingKeywords.length > 0) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Keyword Impact</h3>
          <KeywordSummary />
        </div>
      )}

      {/* Share prompt */}
      <div className="bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-lg p-4 text-center">
        <p className="text-primary-900 font-medium mb-2">
          Impressed by the transformation?
        </p>
        <p className="text-sm text-primary-700">
          Share your ATS score improvement on LinkedIn and help fellow job seekers!
        </p>
      </div>
    </div>
  );
};

BeforeAfterView.propTypes = {
  originalResume: PropTypes.shape({
    professionalSummary: PropTypes.string,
    summary: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.string)
  }),
  tailoredResume: PropTypes.shape({
    tailoredContent: PropTypes.shape({
      summary: PropTypes.string,
      skills: PropTypes.arrayOf(PropTypes.string)
    })
  }),
  keywordAnalysis: PropTypes.shape({
    matched: PropTypes.arrayOf(PropTypes.string),
    missing: PropTypes.arrayOf(PropTypes.string),
    added: PropTypes.arrayOf(PropTypes.string)
  }),
  atsScoreOriginal: PropTypes.number,
  atsScoreTailored: PropTypes.number.isRequired
};

export default BeforeAfterView;
